import { ActionButton, Avatar, Tag, dataColumns, formatRelative, type DataColumn } from "@amber/ui";
import { ACCESS_ACTIONS, ROLE_LABELS, type InvitationRow, type MemberRow, type UserRow } from "../../contracts/index";
import { find } from "../actions";
import type { DialogState } from "../dialogs/AccessDialogs";
import { MemberStatus } from "./MemberStatus";

function joinedVia(row: MemberRow): string {
  if (row.kind === "invitation") return "Invitation";
  return row.joined_via === "invitation" ? "Invitation" : "Auto-join domain";
}

export function isMuted(row: MemberRow): boolean {
  return row.kind === "user" ? row.status === "deactivated" : true;
}

const col = dataColumns<MemberRow>();

export function memberColumns({
  youId,
  onDialog,
  onCopy,
}: {
  youId: string;
  onDialog: (state: DialogState) => void;
  onCopy: (row: InvitationRow) => void;
}): DataColumn<MemberRow>[] {
  return [
    col.display({
      id: "name",
      header: "Name",
      meta: { width: 200, stickyCell: true },
      cell: ({ row: { original: row } }) => {
        const muted = isMuted(row);
        return (
          <span className="flex min-w-0 items-center gap-2">
            <Avatar name={row.kind === "user" ? row.display_name : row.email} muted={muted} />
            <span className={muted ? "truncate" : "truncate font-strong text-fg"}>
              {row.kind === "user" ? row.display_name : "—"}
            </span>
            {row.kind === "user" && row.id === youId ? <Tag tone="quiet">you</Tag> : null}
          </span>
        );
      },
    }),
    col.display({
      id: "email",
      header: "Email",
      meta: { width: 220, cellClassName: "font-mono text-md" },
      cell: ({ row }) => row.original.email,
    }),
    col.display({
      id: "role",
      header: "Role",
      meta: { width: 110 },
      cell: ({ row }) => (
        <Tag tone={row.original.role === "admin" ? "accent" : "strong"}>{ROLE_LABELS[row.original.role]}</Tag>
      ),
    }),
    col.display({
      id: "last_active",
      header: "Last active",
      meta: { width: 110 },
      cell: ({ row: { original: row } }) =>
        row.kind === "user" && row.last_active_at ? formatRelative(row.last_active_at) : "—",
    }),
    col.display({
      id: "status",
      header: "Status",
      meta: { width: 120 },
      cell: ({ row }) => <MemberStatus row={row.original} />,
    }),
    col.display({
      id: "joined",
      header: "Joined via",
      meta: { width: 140 },
      cell: ({ row }) => joinedVia(row.original),
    }),
    col.display({
      id: "actions",
      meta: { label: "Actions", width: 230, cellClassName: "overflow-visible" },
      cell: ({ row: { original: row } }) => (
        <span className="flex gap-1.5">
          {row.kind === "user" ? (
            <UserActions row={row} onDialog={onDialog} />
          ) : (
            <InvitationActions row={row} onDialog={onDialog} onCopy={onCopy} />
          )}
        </span>
      ),
    }),
  ];
}

function UserActions({ row, onDialog }: { row: UserRow; onDialog: (state: DialogState) => void }) {
  if (row.status === "deactivated") {
    return (
      <ActionButton
        action={find(row.actions, ACCESS_ACTIONS.reactivate)}
        label="Reactivate…"
        onClick={() => {
          onDialog({ kind: "reactivate", user: row });
        }}
      />
    );
  }
  return (
    <>
      <ActionButton
        action={find(row.actions, ACCESS_ACTIONS.changeRole)}
        label="Change role…"
        onClick={() => {
          onDialog({ kind: "role", user: row });
        }}
      />
      <ActionButton
        action={find(row.actions, ACCESS_ACTIONS.deactivate)}
        label="Deactivate…"
        onClick={() => {
          onDialog({ kind: "deactivate", user: row });
        }}
      />
    </>
  );
}

function InvitationActions({
  row,
  onDialog,
  onCopy,
}: {
  row: InvitationRow;
  onDialog: (state: DialogState) => void;
  onCopy: (row: InvitationRow) => void;
}) {
  return (
    <>
      {row.expired ? null : (
        <ActionButton
          action={find(row.actions, ACCESS_ACTIONS.copyInvitationLink)}
          label="Copy link"
          onClick={() => {
            onCopy(row);
          }}
        />
      )}
      <ActionButton
        action={find(row.actions, ACCESS_ACTIONS.revokeInvitation)}
        label="Revoke…"
        onClick={() => {
          onDialog({ kind: "revoke", invitation: row });
        }}
      />
    </>
  );
}
